/**
 * LiveActivityFeed - FOMO Component
 *
 * Real-time activity notifications showing user actions.
 * "User ***45 just became a node operator"
 * Uses HVME mediakit colors - 10X Zama!
 */

import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState, useCallback, forwardRef } from "react";

export interface ActivityItem {
  id: string;
  type: "node_operator" | "token_purchase" | "waitlist_join" | "staking";
  user: string;
  amount?: string;
  timestamp: Date;
}

interface LiveActivityFeedProps {
  /** Activity items to display */
  activities?: ActivityItem[];
  /** Maximum items to show at once */
  maxItems?: number;
  /** Position on screen */
  position?: "bottom-left" | "bottom-right" | "top-right";
  /** Auto-dismiss time in ms */
  dismissAfter?: number;
  /** Demo mode - generate fake activities */
  demoMode?: boolean;
  /** Custom class name */
  className?: string;
}

// Demo data for generating fake activities
const DEMO_USERS = [
  "***45", "***78", "***12", "***99", "***23", "***67", "***34", "***56", "***89", "***01"
];

const DEMO_ACTIONS: Array<{ type: ActivityItem["type"]; message: string }> = [
  { type: "node_operator", message: "became a Genesis Node Operator" },
  { type: "token_purchase", message: "secured their $HVME allocation" },
  { type: "waitlist_join", message: "joined the waitlist" },
  { type: "staking", message: "staked for early access" },
];

function generateDemoActivity(): ActivityItem {
  const user = DEMO_USERS[Math.floor(Math.random() * DEMO_USERS.length)] ?? "***42";
  const action = DEMO_ACTIONS[Math.floor(Math.random() * DEMO_ACTIONS.length)] ?? DEMO_ACTIONS[0]!;

  return {
    id: Math.random().toString(36).substring(7),
    type: action.type,
    user: `User ${user}`,
    timestamp: new Date(),
  };
}

function getActivityIcon(type: ActivityItem["type"]) {
  switch (type) {
    case "node_operator":
      return "🖥️";
    case "token_purchase":
      return "💎";
    case "waitlist_join":
      return "✨";
    case "staking":
      return "🔒";
    default:
      return "🎉";
  }
}

function getActivityMessage(item: ActivityItem) {
  switch (item.type) {
    case "node_operator":
      return "became a Genesis Node Operator";
    case "token_purchase":
      return item.amount
        ? `secured ${item.amount} $HVME`
        : "secured their $HVME allocation";
    case "waitlist_join":
      return "joined the waitlist";
    case "staking":
      return item.amount ? `staked ${item.amount}` : "staked for early access";
    default:
      return "joined HVME";
  }
}

function getTimeAgo(date: Date): string {
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (seconds < 5) return "just now";
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  return `${Math.floor(minutes / 60)}h ago`;
}

const ActivityNotification = forwardRef<
  HTMLDivElement,
  { item: ActivityItem; onDismiss: () => void }
>(function ActivityNotification({ item, onDismiss }, ref) {
  const [timeAgo, setTimeAgo] = useState(getTimeAgo(item.timestamp));

  useEffect(() => {
    const interval = setInterval(() => {
      setTimeAgo(getTimeAgo(item.timestamp));
    }, 1000);
    return () => clearInterval(interval);
  }, [item.timestamp]);

  return (
    <motion.div
      ref={ref}
      layout
      initial={{ opacity: 0, x: 100, scale: 0.8 }}
      animate={{ opacity: 1, x: 0, scale: 1 }}
      exit={{ opacity: 0, x: 100, scale: 0.8 }}
      transition={{ type: "spring", stiffness: 400, damping: 30 }}
      onClick={onDismiss}
      className="cursor-pointer group"
    >
      <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-hvme-deep-ink/90 backdrop-blur-lg border border-hvme-aurora-lilac/20 shadow-lg hover:border-hvme-aurora-lilac/40 transition-colors"
        style={{
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3), 0 0 20px rgba(230, 190, 250, 0.1)",
        }}
      >
        {/* Icon */}
        <span className="text-lg">{getActivityIcon(item.type)}</span>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <p className="text-sm text-hvme-near-white">
            <span className="font-semibold text-hvme-aurora-lilac">{item.user}</span>{" "}
            <span className="text-hvme-soft-cloud">{getActivityMessage(item)}</span>
          </p>
          <p className="text-xs text-hvme-text-muted mt-0.5">{timeAgo}</p>
        </div>

        {/* Dismiss hint */}
        <span className="text-xs text-hvme-text-muted opacity-0 group-hover:opacity-100 transition-opacity">
          ×
        </span>
      </div>
    </motion.div>
  );
});

export function LiveActivityFeed({
  activities: externalActivities,
  maxItems = 3,
  position = "bottom-right",
  dismissAfter = 5000,
  demoMode = false,
  className = "",
}: LiveActivityFeedProps) {
  const [activities, setActivities] = useState<ActivityItem[]>(externalActivities || []);

  // Auto-dismiss activities after timeout
  useEffect(() => {
    if (activities.length === 0) return;

    const timer = setTimeout(() => {
      setActivities((prev) => prev.slice(1));
    }, dismissAfter);

    return () => clearTimeout(timer);
  }, [activities, dismissAfter]);

  // Demo mode: generate fake activities periodically with realistic timing
  useEffect(() => {
    if (!demoMode) return;

    let timeoutId: ReturnType<typeof setTimeout>;

    const scheduleNextActivity = () => {
      // Realistic timing: 15-45 seconds between activities
      // This mimics real user signup patterns
      const minDelay = 15000; // 15 seconds
      const maxDelay = 45000; // 45 seconds
      const delay = minDelay + Math.random() * (maxDelay - minDelay);

      timeoutId = setTimeout(() => {
        const newActivity = generateDemoActivity();
        setActivities((prev) => [...prev.slice(-(maxItems - 1)), newActivity]);
        scheduleNextActivity(); // Schedule the next one
      }, delay);
    };

    // Initial activity after 5-10 seconds (don't appear too eager)
    const initialDelay = 5000 + Math.random() * 5000;
    timeoutId = setTimeout(() => {
      const newActivity = generateDemoActivity();
      setActivities((prev) => [...prev.slice(-(maxItems - 1)), newActivity]);
      scheduleNextActivity();
    }, initialDelay);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [demoMode, maxItems]);

  // Sync with external activities
  useEffect(() => {
    if (externalActivities) {
      setActivities(externalActivities.slice(-maxItems));
    }
  }, [externalActivities, maxItems]);

  const dismissActivity = useCallback((id: string) => {
    setActivities((prev) => prev.filter((a) => a.id !== id));
  }, []);

  // Position classes
  const positionClasses = {
    "bottom-left": "fixed bottom-4 left-4",
    "bottom-right": "fixed bottom-4 right-4",
    "top-right": "fixed top-20 right-4",
  };

  return (
    <div className={`${positionClasses[position]} z-50 flex flex-col gap-2 max-w-sm ${className}`}>
      <AnimatePresence mode="popLayout">
        {activities.map((activity) => (
          <ActivityNotification
            key={activity.id}
            item={activity}
            onDismiss={() => dismissActivity(activity.id)}
          />
        ))}
      </AnimatePresence>
    </div>
  );
}

export default LiveActivityFeed;
